import { ListingData } from './types';
import { StyledListingCard } from './style';
import {
  StatusTag,
  TransactionTypeTag,
} from '../../components/ListingTags/style';
import { useIntl } from 'react-intl';
import { Button } from '../../../ui';
import { useNavigate } from 'react-router-dom';
import PublicRoute from 'enums/publicRoutes';
import { adminPath } from 'enums/adminRoutes';
import { Divider } from 'antd';
import { ListingPublicationPeriod } from '../../components/ListingPublicationPeriod';
import dayjs from 'dayjs';

interface ListingCardProps {
  listing: ListingData;
}

export const ListingCard = ({ listing }: ListingCardProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const handleOpenListing = () => {
    if (listing.farm_application_id) {
      navigate(`${adminPath.listings()}?applicationId=${listing.farm_application_id}`);
    } else {
      navigate(`${PublicRoute.WHOLESALE_LISTINGS}/${listing.id}`);
    }
  };

  const getButtonLabel = () => {
    if (listing.farm_application_time_at || listing.is_pending_submitting) {
      return `${intl.formatMessage({ id: 'listing.application.created' })}: ${dayjs(listing.farm_application_time_at).format('DD.MM.YYYY')}`;
    }

    return intl.formatMessage({ id: 'listing.open' });
  };

  const getDeliveryPlace = () => {
    return listing.address || null;
  };

  const getEditedText = () => {
    if (!listing.updated_by_user_at) {
      return null;
    }
    const transactionTypeText = intl.formatMessage({
      id: `listing.edited.${listing.transaction_type}`,
    });
    const editedText = intl.formatMessage({ id: 'listing.edited' });
    return `${transactionTypeText} ${editedText} ${dayjs(listing.updated_by_user_at).format('DD.MM.YYYY')}`;
  };

  const getProductsText = () => {
    if (!listing.products || listing.products.length === 0) {
      return null;
    }

    return listing.products
      .map(
        (product) =>
          `${product.name || product.category?.name} (${product.required_quantity} ${product.storage_unit.short})`
      )
      .join(', ');
  };

  return (
    <StyledListingCard>
      <div className="listing-card-header">
        <div className="header-left">
          <span className="listing-title">{listing.title}</span>
        </div>
        <div className="header-right">
          <TransactionTypeTag $transactionType={listing.transaction_type}>
            {intl.formatMessage({
              id: `listing.transaction_type.public.${listing.transaction_type}`,
            })}
          </TransactionTypeTag>
          <span className="identifier">{listing.identifier}</span>
          <StatusTag $status={listing.status}>
            {intl.formatMessage({
              id: `listing.status.public.${listing.status}`,
            })}
          </StatusTag>
        </div>
      </div>

      {listing.description && (
        <div className="listing-description-row">
          <span className="description-label">
            {intl.formatMessage({ id: 'listing.description' })}:
          </span>
          <span className="description-text">{listing.description}</span>
        </div>
      )}

      <Divider />

      <div className="listing-content">
        <ListingPublicationPeriod
          publicationStartDate={listing.publication_start_date}
          publicationEndDate={listing.publication_end_date}
        />
        {getEditedText() && (
          <div className="edited-row">
            <span className="edited-text">{getEditedText()}</span>
          </div>
        )}

        <div className="products-and-delivery">
          <div className="products-delivery-info">
            {getProductsText() && (
              <div className="products-row">
                <span className="products-label">
                  {intl.formatMessage({ id: 'listing.products' })}:
                </span>
                <span className="products-text">{getProductsText()}</span>
              </div>
            )}
            {getDeliveryPlace() && (
              <div className="delivery-row">
                <span className="delivery-label">
                  {intl.formatMessage({ id: 'listing.delivery_place' })}:
                </span>
                <span className="delivery-text">{getDeliveryPlace()}</span>
              </div>
            )}
            {listing.farm_name && (
              <div className="farm-row">
                <span className="farm-label">
                  {intl.formatMessage({ id: 'listing.farm_name' })}:
                </span>
                <span className="farm-text">{listing.farm_name}</span>
              </div>
            )}
          </div>
          <div className="button-container">
            <Button label={getButtonLabel()} onClick={handleOpenListing} />
          </div>
        </div>
      </div>
    </StyledListingCard>
  );
};
